import { useMemo, useState } from 'react';
import { Button } from '@xiltrion/ui/button';
import { Checkbox } from '@xiltrion/ui/checkbox';
import { Icono } from '@xiltrion/ui/icono';
import { Label } from '@xiltrion/ui/label';
import { Timeline, TimelineContent, TimelineDate, TimelineHeader, TimelineIndicator, TimelineItem, TimelineSeparator, TimelineTitle } from '@xiltrion/ui/timeline';
import type { TimelineItem as ActivityEntry } from '@/api/types';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui';
import { useCaseTimeline } from '../api';
import { ACTOR_KIND } from '../lib';

const dayKey = (iso: string) => new Date(iso).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
const time = (iso: string) => new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
const DONE = new Set(['CASE_CREATED', 'RADICATION_GENERATED', 'AI_DECISION_CREATED', 'CASE_CLASSIFIED', 'CASE_ASSIGNED', 'TASK_COMPLETED', 'TASK_APPROVED', 'CASE_CLOSED', 'NOTIFICATION_SENT', 'COMMUNICATION_OUT', 'DOCUMENT_UPLOADED', 'CASE_INDEX_SIGNED', 'COURT_ORDER_REGISTERED', 'SENT_TO_COURT', 'COURT_RULING_RECORDED', 'COURT_RULING_COMPLIED', 'SUPPORT_ANSWERED', 'AREA_TRANSFER_APPROVED', 'CASE_REOPEN_APPROVED']);
const ALERT = new Set(['SLA_BREACHED', 'SLA_ESCALATED', 'TASK_REJECTED', 'AI_DECISION_FAILED', 'TASK_BLOCKED', 'CASE_INDEX_SIGNATURE_FAILED']);
/** Every entry already happened, so all items are "completed"; the mark colour tells done / alert / neutral and the line stays neutral. */
const MARK = {
  alert: { icon: 'mdi:alert', label: 'alerta', cls: 'border-destructive bg-destructive text-destructive-foreground group-data-[completed]/timeline-item:border-destructive' },
  done: { icon: 'mdi:check', label: 'completado', cls: 'border-success bg-success text-success-foreground group-data-[completed]/timeline-item:border-success' },
  neutral: { icon: 'mdi:circle-small', label: null, cls: 'border-primary bg-primary text-primary-foreground group-data-[completed]/timeline-item:border-primary' },
} as const;

/** Actividad tab: readable timeline grouped by day, actor kinds visually distinct. */
export function ActivityTab({ caseId }: { caseId: string }) {
  const q = useCaseTimeline(caseId);
  const [kinds, setKinds] = useState<Set<string>>(new Set(Object.keys(ACTOR_KIND).filter((k) => k !== 'SYSTEM')));
  const groups = useMemo(() => {
    const items = (q.data ?? []).filter((i) => kinds.has(i.kind)).slice().reverse();
    const map = new Map<string, ActivityEntry[]>();
    for (const it of items) { const k = dayKey(it.at); map.set(k, [...(map.get(k) ?? []), it]); }
    return [...map.entries()];
  }, [q.data, kinds]);
  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar la actividad." />;
  if (!q.data?.length) return <EmptyState title="Todavía no hay actividad registrada." hint="Cada paso del proceso, comentario y comunicación quedará aquí." icon="mdi:timeline-clock-outline" />;
  return (
    <div className="flex flex-col gap-4">
      <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2 border-0 m-0 p-0">
        <legend className="sr-only">Filtrar por tipo de actor</legend>
        {Object.entries(ACTOR_KIND).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <Checkbox id={`act-kind-${k}`} checked={kinds.has(k)} onCheckedChange={(c) => setKinds((prev) => { const next = new Set(prev); if (c === true) next.add(k); else next.delete(k); return next; })} />
            <Label htmlFor={`act-kind-${k}`} className="text-sm font-normal">{v.label}</Label>
          </div>
        ))}
      </fieldset>
      {groups.map(([day, items]) => (
        <section key={day} aria-label={day}>
          <h2 className="text-xs font-normal uppercase tracking-wide text-muted-foreground m-0 mb-2">{day}</h2>
          <Timeline value={items.length} role="list">{items.map((it, i) => <Item key={it.id} it={it} step={i + 1} />)}</Timeline>
        </section>
      ))}
    </div>
  );
}

function Item({ it, step }: { it: ActivityEntry; step: number }) {
  const [open, setOpen] = useState(false);
  const kind = ACTOR_KIND[it.kind] ?? ACTOR_KIND.SYSTEM!;
  const mark = MARK[ALERT.has(it.event) ? 'alert' : DONE.has(it.event) ? 'done' : 'neutral'];
  const details = it.details ?? {};
  const body = typeof details.body === 'string' ? details.body : null;
  const reason = typeof details.reason === 'string' && details.reason ? details.reason : null;
  const hasMore = Object.values(details).some((v) => v !== null && v !== undefined && !(typeof v === 'object' && v !== null && Object.keys(v as object).length === 0));
  return (
    <TimelineItem step={step} role="listitem" className="group-data-[orientation=vertical]/timeline:ms-10 group-data-[orientation=vertical]/timeline:not-last:pb-4">
      <TimelineHeader className="flex items-center gap-2 flex-wrap">
        <TimelineSeparator className="bg-border! group-data-[orientation=vertical]/timeline:-left-7 group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
        <TimelineDate dateTime={it.at} className="mb-0 tnum whitespace-nowrap min-w-[4.5rem] text-sm font-normal">{time(it.at)}</TimelineDate>
        <span className={`text-xs rounded px-1.5 py-0.5 ${kind.cls} inline-flex items-center gap-1`}><Icono nombre={kind.icon} className="size-3.5" aria-hidden /> {it.actorName ?? kind.label}</span>
        <TimelineTitle>{it.title}{mark.label && <span className="sr-only"> ({mark.label})</span>}</TimelineTitle>
        <TimelineIndicator className={`flex size-6 items-center justify-center border-2 group-data-[orientation=vertical]/timeline:-left-7 ${mark.cls}`}><Icono nombre={mark.icon} className="size-4" /></TimelineIndicator>
      </TimelineHeader>
      {(it.subtitle || reason || hasMore || body) && (
        <TimelineContent className="mt-1 flex flex-col items-start gap-1">
          {it.subtitle && <div className="text-sm text-muted-foreground">{it.subtitle}</div>}
          {reason && <div>{reason}</div>}
          {hasMore && !body && <Button variant="link" size="xs" onClick={() => setOpen((o) => !o)} aria-expanded={open}>{open ? 'Ocultar' : 'Detalles'}</Button>}
          {body && <div className="text-sm text-foreground bg-warning/10 border border-warning/30 rounded p-2 whitespace-pre-wrap max-w-2xl">{body}</div>}
          {open && !body && <pre className="font-sans m-0 text-xs bg-muted border rounded p-2 overflow-auto max-w-2xl w-full">{JSON.stringify(details, null, 2)}</pre>}
        </TimelineContent>
      )}
    </TimelineItem>
  );
}
